import React from 'react';
import TopNavigation from '../components/TopNavigation';
import PricingTiers from '../components/PricingTiers';
import AppStorePlans from '../components/AppStorePlans';
import { useTheme } from '@/contexts/ThemeContext';
import { isNativeApp } from '@/utils/platform';

interface PricingPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  showMenu: boolean;
  onToggleMenu: () => void;
  isAuthenticated: boolean;
}

const PricingPage: React.FC<PricingPageProps> = ({
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateToMyBooks,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onToggleMenu,
  isAuthenticated,
}) => {
  // Since 1.2 the native app sells its subscriptions in-app, so this page
  // renders there too — with the App Store's own prices (AppStorePlans)
  // rather than the web tiers, which read /api/pricing.
  const native = isNativeApp();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToMyBooks={onNavigateToMyBooks}
        showProfileIcon={isAuthenticated}
      />
      <div className="w-full px-6 pt-12 sm:px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        <header className="mx-auto mb-20 w-full max-w-6xl">
          <h1 className={`portrait-text-app mb-4 text-left text-4xl font-bold sm:text-5xl lg:text-6xl ${
            isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
          }`}>
            Pricing
          </h1>
          <p className={`max-w-4xl pt-4 text-md leading-relaxed sm:text-lg md:text-xl ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'}`}>
            Start free, upgrade when you want more time with Aleph. Audiobook listening is
            unlimited on every plan.
          </p>
        </header>

        <div className="mx-auto w-full max-w-6xl">
          {native ? (
            <AppStorePlans
              variant="page"
              source="pricing_page"
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacy={onNavigateToPrivacy}
            />
          ) : (
            <PricingTiers variant="page" source="pricing_page" />
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
